import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, Button, Input, Label } from "@learn-me/ui";
import { useCourseWizard } from "@/hooks";
import { adminApi } from "../../api/adminApi";
import {
  Sparkles,
  Loader2,
  GripVertical,
  Trash2,
  Plus,
  Edit2,
  Check,
  X,
  Clock,
  RefreshCw,
  Unlock,
  Lock,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ChapterData {
  title: string;
  description: string;
  estimatedDuration: number;
  includeQuiz: boolean;
}

interface SortableChapterProps {
  chapter: ChapterData;
  index: number;
  onUpdate: (index: number, data: Partial<ChapterData>) => void;
  onRemove: (index: number) => void;
  isFree: boolean;
}

function SortableChapter({
  chapter,
  index,
  onUpdate,
  onRemove,
  isFree,
}: SortableChapterProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(chapter.title);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `chapter-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleSave = () => {
    if (editTitle.trim()) {
      onUpdate(index, { title: editTitle.trim() });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditTitle(chapter.title);
    setIsEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border rounded-lg p-4 ${isDragging ? "opacity-50 shadow-lg" : ""}`}
    >
      <div className="flex items-start gap-3">
        {/* Drag handle */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="mt-1 cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="h-5 w-5" />
        </button>

        {/* Chapter number */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-medium">
          {index + 1}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="flex-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") handleCancel();
                }}
              />
              <Button variant="ghost" size="sm" onClick={handleSave}>
                <Check className="h-4 w-4 text-green-600" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4 text-gray-500" />
              </Button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-gray-900">{chapter.title}</h4>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">
                {chapter.description}
              </p>
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="flex items-center gap-1 text-gray-500">
              <Clock className="h-3 w-3" />
              {chapter.estimatedDuration} min
            </span>
            {isFree ? (
              <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                <Unlock className="h-3 w-3" />
                Gratuito
              </span>
            ) : (
              <span className="flex items-center gap-1 text-gray-500">
                <Lock className="h-3 w-3" />
                De pago
              </span>
            )}
            <label className="flex items-center gap-1 text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={chapter.includeQuiz}
                onChange={(e) =>
                  onUpdate(index, { includeQuiz: e.target.checked })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Quiz
            </label>
          </div>
        </div>

        {/* Delete button */}
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-1 text-gray-400 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function WizardStep3Structure() {
  const {
    topic,
    level,
    duration,
    additionalNotes,
    referenceFiles,
    referenceText,
    courseTitle,
    setCourseTitle,
    courseDescription,
    setCourseDescription,
    shortDescription,
    setShortDescription,
    chapters,
    setChapters,
    updateChapter,
    removeChapter,
    addChapter,
    isOutlineGenerated,
    setIsOutlineGenerated,
    isGeneratingOutline,
    setIsGeneratingOutline,
  } = useCourseWizard();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const generateMutation = useMutation({
    mutationFn: async () => {
      // Build reference context from files and text
      let referenceContext = "";
      if (referenceText) {
        referenceContext += referenceText + "\n\n";
      }
      // Note: File content would need to be extracted server-side
      // For now, we pass the file names as hints
      if (referenceFiles.length > 0) {
        referenceContext +=
          "Documentos subidos: " + referenceFiles.map((f) => f.name).join(", ");
      }

      return adminApi.generateOutline({
        topic,
        duration,
        level,
        additionalNotes: additionalNotes || undefined,
        referenceContext: referenceContext || undefined,
      });
    },
    onSuccess: (data) => {
      setCourseTitle(data.title);
      setCourseDescription(data.description);
      setShortDescription(data.shortDescription);
      setChapters(
        data.chapters.map((ch) => ({
          title: ch.title,
          description: ch.description,
          estimatedDuration: ch.estimatedDuration,
          includeQuiz: true,
        })),
      );
      setIsOutlineGenerated(true);
      setIsGeneratingOutline(false);
      toast.success("Estructura generada exitosamente");
    },
    onError: (error) => {
      setIsGeneratingOutline(false);
      toast.error(
        error instanceof Error
          ? error.message
          : "Error al generar la estructura",
      );
    },
  });

  const handleGenerate = () => {
    setIsGeneratingOutline(true);
    generateMutation.mutate();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = parseInt(String(active.id).replace("chapter-", ""));
    const newIndex = parseInt(String(over.id).replace("chapter-", ""));

    const newChapters = arrayMove(chapters, oldIndex, newIndex);
    setChapters(newChapters);
  };

  const handleAddChapter = () => {
    addChapter({
      title: `Capitulo ${chapters.length + 1}`,
      description: "Nuevo capitulo",
      estimatedDuration: 15,
      includeQuiz: true,
    });
  };

  if (!isOutlineGenerated && !isGeneratingOutline) {
    return (
      <div className="max-w-xl mx-auto text-center">
        <Card>
          <CardContent className="pt-6 pb-8">
            <div className="p-4 bg-primary-50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Generar estructura del curso
            </h3>
            <p className="text-gray-600 mb-6">
              La AI creara automaticamente la estructura del curso basandose en
              el tema "{topic}" y los parametros que configuraste.
              {referenceFiles.length > 0 &&
                ` Se usaran ${referenceFiles.length} documento(s) de referencia.`}
            </p>
            <Button
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
              className="min-w-[200px]"
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generar estructura
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isGeneratingOutline) {
    return (
      <div className="max-w-xl mx-auto text-center">
        <Card>
          <CardContent className="pt-8 pb-10">
            <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary-500" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Generando estructura...
            </h3>
            <p className="text-gray-600">
              Esto puede tomar unos segundos. La AI esta analizando tu tema y
              creando la mejor estructura posible.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Course info */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Informacion del curso</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Regenerar
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="courseTitle">Titulo del curso</Label>
              <Input
                id="courseTitle"
                value={courseTitle}
                onChange={(e) => setCourseTitle(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="courseDescription">Descripcion</Label>
              <textarea
                id="courseDescription"
                value={courseDescription}
                onChange={(e) => setCourseDescription(e.target.value)}
                className="mt-1 w-full min-h-[100px] p-3 border rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="shortDescription">Descripcion corta</Label>
              <Input
                id="shortDescription"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                maxLength={200}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {shortDescription.length}/200 caracteres
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chapters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">
              Capitulos ({chapters.length})
            </h3>
            <Button variant="outline" size="sm" onClick={handleAddChapter}>
              <Plus className="h-4 w-4 mr-2" />
              Agregar capitulo
            </Button>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={chapters.map((_, i) => `chapter-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {chapters.map((chapter, index) => (
                  <SortableChapter
                    key={`chapter-${index}`}
                    chapter={chapter}
                    index={index}
                    onUpdate={updateChapter}
                    onRemove={removeChapter}
                    isFree={index === 0}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {chapters.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No hay capitulos. Agrega al menos uno para continuar.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Total de capitulos:</span>
          <span className="font-medium">{chapters.length}</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-gray-600">Duracion estimada:</span>
          <span className="font-medium">
            {chapters.reduce((acc, ch) => acc + ch.estimatedDuration, 0)}{" "}
            minutos
          </span>
        </div>
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-gray-600">Capitulos con quiz:</span>
          <span className="font-medium">
            {chapters.filter((ch) => ch.includeQuiz).length}
          </span>
        </div>
      </div>
    </div>
  );
}
